import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Collapse identical GETs that are already in flight. This prevents multiple
// mounted UI surfaces (or development remounts) from issuing the same request
// at the same time while keeping GET responses uncached once they settle.
const inFlightGets = new Map();
const axiosGet = api.get.bind(api);
api.get = (url, config) => {
  let key;
  try {
    key = JSON.stringify([url, config?.params || null]);
  } catch {
    key = String(url);
  }
  const existing = inFlightGets.get(key);
  if (existing) return existing;
  const request = axiosGet(url, config).finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, request);
  return request;
};

export function mediaUrl(path) {
  if (!path) return null;
  return path.startsWith("http") ? path : `${BACKEND_URL}${path}`;
}

// Text is HTML-escaped server-side before storage; React escapes again on render,
// so decoding here is safe and stops users seeing raw &gt; / &amp; entities.
export function decodeText(text) {
  if (!text) return "";
  return text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, "/")
    .replace(/&amp;/g, "&");
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// The Public Chat rail historically expected the suggestions endpoint to return
// an array, while the real endpoint returns {items, total, has_more}. Normalize
// only that exact legacy call so Discover and every other consumer keep the
// richer response shape.
api.interceptors.response.use(
  (response) => {
    if (response.config?.url === "/users/suggestions?limit=8" && response.data && !Array.isArray(response.data)) {
      response.data = response.data.items || [];
    }
    return response;
  },
  (error) => {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail && typeof detail === "object") {
      if (detail.code === "banned") window.dispatchEvent(new CustomEvent("yaprio:banned", { detail }));
      if (detail.code === "ip_banned") window.dispatchEvent(new CustomEvent("yaprio:ip-banned", { detail }));
    }
    return Promise.reject(error);
  }
);

export default api;
